import {
    INoEditService, NoEditServiceProps, PluginClass,
    PluginConfig,
} from '@src/slide/application/no-edit/service/no-edit-service/no-edit-service.interface';
import { INoEditPlugin } from '@src/slide/application/no-edit/plugins/no-edit-plugin.interface';

/**
 * 非编辑服务。
 * 主要负责管理所有非编辑plugin
 */
export class NoEditService implements INoEditService {
    /**
     * 存储plugin注册配置信息
     */
    private pluginConfigMap: Map<string, PluginConfig>;
    /**
     * 存储plugin实例
     */
    private pluginMap: Map<string, INoEditPlugin>;

    private props: NoEditServiceProps;

    public constructor(props: NoEditServiceProps) {
        this.pluginConfigMap = new Map<string, PluginConfig>();
        this.pluginMap = new Map<string, INoEditPlugin>();

        this.props = props;
    }

    /**
     * 插件批量注册
     * @param configs
     */
    public batchRegisterPlugins(configs: PluginConfig[]): void {
        configs.forEach((config) => {
            this.registerPlugin(config);
        });
    }

    /**
     * 注册plugin。
     * @param config
     * @throws 如果存在相同name的plugin，会抛出异常
     */
    public registerPlugin(config: PluginConfig): void {
        if (this.pluginConfigMap.has(config.name)) {
            throw new Error(`plugin ${config.name} has been registered`);
        }
        this.pluginConfigMap.set(config.name, config);
    }

    /**
     * 取消注册插件。如果插件已经初始化会触发其dispose方法
     * @param pluginName
     */
    public unRegisterPlugin(pluginName: string): void {
        if (this.pluginConfigMap.has(pluginName)) {
            this.pluginConfigMap.delete(pluginName);
            const pluginObj = this.pluginMap.get(pluginName);
            if (pluginObj) {
                this.pluginMap.delete(pluginName);
                pluginObj.dispose();
            }
        }
    }

    /**
     * 动态获取指定插件。未创建会先创建，同时会调用initPlugin方法进行初始化
     * @param pluginName
     */
    public async getPlugin<T extends INoEditPlugin>(pluginName: string): Promise<T | undefined> {
        const pluginConfig = this.pluginConfigMap.get(pluginName);
        if (pluginConfig) {
            let pluginObj = this.pluginMap.get(pluginName);
            if (!pluginObj) {
                pluginObj = await this.createPlugin(pluginConfig);
                if (this.pluginMap.get(pluginName)) { // 如果异步回来，发现已经有值，直接返回该值
                    return this.pluginMap.get(pluginName) as T;
                }
                pluginObj.initPlugin();  // 获取plugin，调用初始化方法
                this.pluginMap.set(pluginName, pluginObj);
            }
            return pluginObj as T;
        }
    }

    /**
     * 同步获取指定plugin对象。未初始化会先初始化。
     * 如果同步找不到返回undefined
     * @param pluginName
     */
    public getPluginSync<T extends INoEditPlugin>(pluginName: string): T | undefined {
        const pluginConfig = this.pluginConfigMap.get(pluginName);
        if (pluginConfig) {
            let pluginObj = this.pluginMap.get(pluginName);
            if (!pluginObj) {
                let pluginClass: PluginClass;
                if (this.isPluginClass(pluginConfig.plugin)) {
                    pluginClass = pluginConfig.plugin as PluginClass;
                    pluginObj = new pluginClass({
                        noEditService: this,
                        extensionService: this.props.extensionService,
                    });
                    pluginObj.initPlugin();  // 获取plugin，调用初始化方法
                    this.pluginMap.set(pluginName, pluginObj);
                }
            }
            return pluginObj as T;
        }
    }

    /**
     * 手动初始化指定plugin
     * @param pluginName
     */
    public async initPlugin(pluginName: string): Promise<void> {
        await this.getPlugin(pluginName);
    }

    /**
     * 同步初始化指定plugin
     * @param name
     */
    public initPluginSync(name: string): void {
        this.getPluginSync(name);
    }

    /**
     * 根据config，创建相应的plugin实例
     * @param pluginConfig
     */
    private async createPlugin(pluginConfig: PluginConfig): Promise<INoEditPlugin> {
        let pluginClass: PluginClass;
        if (this.isPluginClass(pluginConfig.plugin)) {
            pluginClass = pluginConfig.plugin as PluginClass;
        } else {
            const promiseFn = pluginConfig.plugin as (() => Promise<PluginClass>);
            pluginClass = await promiseFn();
        }
        return new pluginClass({
            noEditService: this,
            extensionService: this.props.extensionService,
        });
    }

    /**
     * 判断是否是Class类
     * @param plugin
     */
    private isPluginClass(plugin): boolean {
        return typeof plugin.pluginTag !== 'undefined';
    }
}
